/**
 * Build Form Prompt
 *
 * Helps create forms following Classic Theme patterns.
 */

import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import { z } from 'zod';

const BuildFormSchema = z.object({
  formType: z.string().describe('Type of form (login, registration, contact, settings, custom)'),
  fields: z
    .string()
    .describe('Comma-separated list of field names (e.g., "email, password, name")'),
  includeValidation: z
    .string()
    .optional()
    .describe('Whether to include Zod validation schema (true/false)'),
});

/**
 * Register the build_form prompt
 */
export function registerBuildFormPrompt(server: McpServer): void {
  server.prompt(
    'build_form',
    'Create a form following Classic Theme patterns with proper validation',
    BuildFormSchema.shape,
    async (params) => {
      const parsed = BuildFormSchema.parse(params);
      const formType = parsed.formType || 'custom';
      const fields = (parsed.fields || '').split(',').map((f: string) => f.trim());
      const shouldIncludeValidation = parsed.includeValidation !== 'false';

      const fieldList = fields.map((f) => `- ${f}`).join('\n');

      return {
        messages: [
          {
            role: 'user',
            content: {
              type: 'text',
              text: `Create a ${formType} form with these fields:
${fieldList}

Requirements:
- Import components from '@classic-homes/theme-svelte'
- Use the FormField component for each field
- Use the useForm composable for state management
${shouldIncludeValidation ? '- Include Zod schema validation using schemas from @classic-homes/theme-svelte' : ''}
- Handle form submission with loading state
- Show error messages for validation failures
- Follow Svelte 5 runes syntax ($props, $state, $derived)

Available form components:
- FormField: Combines label, input, error message, and hint text
- Input: Text input with value binding
- Textarea: Multi-line text input
- Select: Dropdown selection
- Checkbox: Boolean checkbox
- RadioGroup: Radio button group
- DateTimePicker: Date and time selection
- FileUpload: File upload with progress

Available validation schemas (from @classic-homes/theme-svelte):
- emailSchema: Email validation
- passwordSchema: Strong password (8+ chars, mixed case, numbers, special)
- loginPasswordSchema: Simpler password for login
- usernameSchema: Username validation
- fullNameSchema: Name validation
- phoneSchema: Phone number validation

Example structure:
\`\`\`svelte
<script lang="ts">
  import { FormField, Button, useForm } from '@classic-homes/theme-svelte';
  import { z } from 'zod';

  const schema = z.object({
    // field definitions
  });

  const form = useForm({
    schema,
    initialValues: { /* ... */ },
    onSubmit: async (data) => { /* ... */ },
  });
</script>

<form onsubmit={form.handleSubmit}>
  <!-- FormField components -->
  <Button type="submit" loading={form.isSubmitting}>Submit</Button>
</form>
\`\`\``,
            },
          },
        ],
      };
    }
  );
}
